import { ReactNode } from "react";
import { Box, Link } from "../../common/components";
import { createStyles, palette, spacing } from "../../styles/theme";
import { ROUTES } from "../../constant/common.constant";
import { usePathActive } from "../../common/hooks/usePathActive";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const useStyles = (showRightHideLeft: boolean) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    centerContainer: {
      display: "flex",
      flexDirection: "row",
      flex: "1",
      overflow: "auto",
      height: "100%",
      maxHeight: { xs: "calc(100vh - 48px)", lg: "unset" },
    },
    leftPanel: {
      flex: "1 1",
      display: showRightHideLeft ? "none" : "flex",
      flexDirection: "column",
      width: "100%",
      padding: {
        xs: `${spacing.wrapperMobile} ${spacing.wrapperMobile} ${spacing.xlMobile} ${spacing.wrapperMobile}`,
        lg: `${spacing.wrapperDesktop} ${spacing.largeDesktop} ${spacing.xlDesktop} ${spacing.wrapperDesktop}`,
      },
      paddingTop: { xs: 0, lg: "20px" },
      backgroundColor: { xs: "white", lg: palette.grey.lightBackground },
      overflow: "initial",
      alignItems: "flex-end",
      minHeight: { xs: "fit-content", lg: "100vh" },
      maxHeight: { xs: "calc(100vh - 48px)", lg: "unset" },
      overflowY: { xs: "auto", lg: "initial" },
    },
    leftPanelContent: {
      display: "flex",
      flexDirection: "column",
      flex: "1 1 auto",
      minWidth: { xs: undefined, lg: "540px" },
      maxWidth: { xs: undefined, lg: "840px" },
      width: "100%",
      mt: { xs: spacing.wrapperMobile, lg: spacing.regularDesktop },
    },
    rightPanel: {
      display: { xs: showRightHideLeft ? "block" : "none", lg: "block" },
      padding: {
        xs: `0`,
        lg: `${spacing.largeDesktop} ${spacing.wrapperDesktop} ${spacing.xlDesktop} ${spacing.wrapperDesktop}`,
      },
      minWidth: { xs: "100%", lg: "400px" },
      maxWidth: { xs: "100%", lg: "35%" },
      flex: "1 1 auto",
      backgroundColor: "white",
      borderLeft: `1px solid ${palette.grey.lightGrey}`,
      ...(showRightHideLeft
        ? {
            maxHeight: { xs: "calc(100vh - 48px)", lg: "unset" },
            overflowY: "auto",
            overflowX: "hidden",
          }
        : {}),
    },
  });

interface Props {
  leftPanel: ReactNode;
  rightPanel: ReactNode;
}

export const CheckoutTemplate = ({ leftPanel, rightPanel }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useThemeBreakpoints();
  const { checkActive } = usePathActive();
  const showRightHideLeft = checkActive(ROUTES.CHECKOUT) && isMobile;
  const styles = useStyles(showRightHideLeft);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.centerContainer}>
        <Box sx={styles.leftPanel}>
          {!isMobile && (
            <Box fullWidth>
              <Box maxWidth={"fit-content"}>
                <Link
                  startIcon="keyboard_arrow_left"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  {t("general.back")}
                </Link>
              </Box>
            </Box>
          )}
          {!showRightHideLeft && (
            <Box sx={styles.leftPanelContent}>{leftPanel}</Box>
          )}
        </Box>
        <Box sx={styles.rightPanel}>{rightPanel}</Box>
      </Box>
    </Box>
  );
};
